import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchPageBySlug, upsertPageBySlug, clearPageState } from "../redux/slices/pageSlice";

// CKEditor
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const EditPageEditor = () => {
    const { slug: routeSlug } = useParams();
    const dispatch = useDispatch();
    const { pageData, loading, error, success } = useSelector((state) => state.page);

    // ============================================
    // State Variables
    // ============================================
    const [slug, setSlug] = useState(routeSlug || "");
    const [pageTitle, setPageTitle] = useState("");
    const [pageDesc, setPageDesc] = useState("");
    const [content, setContent] = useState("");
    const [metaTitle, setMetaTitle] = useState("");
    const [metaDescription, setMetaDescription] = useState("");
    const [metaKeywords, setMetaKeywords] = useState("");
    const [relatedButtons, setRelatedButtons] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [successMessage, setSuccessMessage] = useState(""); // ✅ NEW: Local success message

    // ============================================
    // Effects
    // ============================================

    // Effect 1: Fetch page data when slug is available
    useEffect(() => {
        if (slug) {
            console.log("📍 Fetching page with slug:", slug);
            dispatch(fetchPageBySlug(slug));
        }
        return () => dispatch(clearPageState());
    }, [dispatch, slug]);

    // Effect 2: Prefill form when page data is loaded
    useEffect(() => {
        if (pageData) {
            console.log("📍 pageData received:", pageData);
            console.log("📍 pageData.faqs:", pageData.faqs);

            setPageTitle(pageData.pageTitle || "");
            setPageDesc(pageData.pageDesc || "");
            setContent(pageData.content || "");
            setMetaTitle(pageData.metaTitle || "");
            setMetaDescription(pageData.metaDescription || "");
            setMetaKeywords((pageData.metaKeywords || []).join(", "));
            setRelatedButtons(pageData.relatedButtons || []);
            setFaqs(pageData.faqs || []); // ✅ Load FAQs

            console.log("📍 FAQs set to:", pageData.faqs || []);
        }
    }, [pageData]);

    // Effect 3: Clear success message after 3 seconds
    useEffect(() => {
        if (success || successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [success, successMessage]);

    // ============================================
    // Related Buttons Handlers
    // ============================================
    const addButton = () =>
        setRelatedButtons([...relatedButtons, { buttonSlug: "", buttonTitle: "" }]);

    const removeButton = (index) => {
        const updated = [...relatedButtons];
        updated.splice(index, 1);
        setRelatedButtons(updated);
    };

    const updateButton = (index, field, value) => {
        const updated = [...relatedButtons];
        updated[index][field] = value;
        setRelatedButtons(updated);
    };

    // ============================================
    // FAQ Handlers
    // ============================================
    const addFaq = () => setFaqs([...faqs, { question: "", answer: "" }]);

    const removeFaq = (index) => {
        const updated = [...faqs];
        updated.splice(index, 1);
        setFaqs(updated);
    };

    const updateFaq = (index, field, value) => {
        const updated = [...faqs];
        updated[index][field] = value;
        setFaqs(updated);
    };

    // ============================================
    // Save Handler
    // ============================================
    const handleSave = () => {
        // Validation
        if (!slug) {
            alert("⚠️ Slug is required!");
            return;
        }

        if (!pageTitle) {
            alert("⚠️ Page Title is required!");
            return;
        }

        if (!content) {
            alert("⚠️ Content is required!");
            return;
        }

        console.log("📍 Saving page with data:", {
            slug,
            pageTitle,
            content,
            faqs,
            relatedButtons,
        });

        dispatch(
            upsertPageBySlug({
                slug,
                pageData: {
                    pageTitle,
                    pageDesc,
                    content,
                    metaTitle,
                    metaDescription,
                    metaKeywords: metaKeywords
                        .split(",")
                        .map((kw) => kw.trim())
                        .filter((kw) => kw),
                    relatedButtons,
                    faqs, // ✅ Save FAQs
                },
            })
        ).then((action) => {
            if (action.type === upsertPageBySlug.fulfilled.type) {
                setSuccessMessage("✅ Page saved successfully!");
                console.log("📍 Page saved successfully!");
            }
        });
    };

    return (
        <div className="container mt-4">
            <div className="card">
                <div className="card-header">
                    <h4 className="mb-3">
                        {routeSlug ? "Edit Page" : "New Page"}
                        {loading && <span className="badge bg-warning ms-2">Loading...</span>}
                    </h4>
                </div>

                <div className="card-body">
                    {/* ✅ NEW: Debug Info (Remove in production) */}
                    <div style={{ backgroundColor: "#f0f0f0", padding: "10px", marginBottom: "20px", borderRadius: "5px" }}>
                        <small>
                            <strong>Debug:</strong> FAQs in state: {faqs.length} | pageData available:{" "}
                            {pageData ? "Yes" : "No"} | pageData.faqs: {pageData?.faqs?.length || 0}
                        </small>
                    </div>

                    {/* Slug */}
                    <div className="mb-3">
                        <label className="form-label">Slug</label>
                        <input
                            type="text"
                            className="form-control"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            placeholder="e.g. json-formatter"
                            disabled={!!routeSlug}
                        />
                        <small className="text-muted">Slug will be used in the page URL</small>
                    </div>

                    {/* Title */}
                    <div className="mb-3">
                        <label className="form-label">Page Title *</label>
                        <input
                            type="text"
                            className="form-control"
                            value={pageTitle}
                            onChange={(e) => setPageTitle(e.target.value)}
                            placeholder="Enter page title"
                        />
                    </div>

                    {/* Description */}
                    <div className="mb-3">
                        <label className="form-label">Page Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={pageDesc}
                            onChange={(e) => setPageDesc(e.target.value)}
                            placeholder="Short description about this page"
                        />
                    </div>

                    {/* Meta Fields */}
                    <div className="mb-3">
                        <label className="form-label">Meta Title</label>
                        <input
                            type="text"
                            className="form-control"
                            value={metaTitle}
                            onChange={(e) => setMetaTitle(e.target.value)}
                            placeholder="Meta Title for SEO"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Meta Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={metaDescription}
                            onChange={(e) => setMetaDescription(e.target.value)}
                            placeholder="Meta Description for SEO"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Meta Keywords (comma separated)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={metaKeywords}
                            onChange={(e) => setMetaKeywords(e.target.value)}
                            placeholder="json, formatter, tool"
                        />
                    </div>

                    {/* Content */}
                    <div className="mb-3">
                        <label className="form-label">Content *</label>
                        <CKEditor
                            editor={ClassicEditor}
                            data={content}
                            onChange={(_, editor) => setContent(editor.getData())}
                        />
                    </div>

                    {/* FAQs Section */}
                    <div className="mb-3">
                        <label className="form-label">
                            FAQs
                            {faqs.length > 0 && <span className="badge bg-info ms-2">{faqs.length}</span>}
                        </label>

                        {faqs.length === 0 ? (
                            <div style={{ padding: "10px", textAlign: "center", color: "#999" }}>
                                <small>No FAQs added yet</small>
                            </div>
                        ) : (
                            faqs.map((faq, index) => (
                                <div key={index} className="mb-2" style={{ backgroundColor: "#f9f9f9", padding: "10px", borderRadius: "5px" }}>
                                    <div className="d-flex mb-2 align-items-start gap-2">
                                        <div style={{ flex: 1 }}>
                                            <small className="text-muted d-block mb-1">FAQ #{index + 1}</small>
                                            <textarea
                                                className="form-control form-control-sm"
                                                placeholder="Question"
                                                value={faq.question}
                                                onChange={(e) => updateFaq(index, "question", e.target.value)}
                                                rows="2"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger mt-4"
                                            onClick={() => removeFaq(index)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <textarea
                                        className="form-control form-control-sm"
                                        placeholder="Answer (HTML supported)"
                                        value={faq.answer}
                                        onChange={(e) => updateFaq(index, "answer", e.target.value)}
                                        rows="3"
                                        style={{ marginBottom: "10px" }}
                                    />
                                </div>
                            ))
                        )}

                        <button type="button" className="btn btn-secondary btn-sm" onClick={addFaq}>
                            + Add FAQ
                        </button>
                    </div>

                    {/* Related Buttons */}
                    <div className="mb-3">
                        <label className="form-label">Related Buttons</label>
                        {relatedButtons.map((btn, index) => (
                            <div className="d-flex mb-2" key={index}>
                                <input
                                    type="text"
                                    className="form-control me-2"
                                    placeholder="Button Slug"
                                    value={btn.buttonSlug}
                                    onChange={(e) => updateButton(index, "buttonSlug", e.target.value)}
                                />
                                <input
                                    type="text"
                                    className="form-control me-2"
                                    placeholder="Button Title"
                                    value={btn.buttonTitle}
                                    onChange={(e) => updateButton(index, "buttonTitle", e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => removeButton(index)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button type="button" className="btn btn-secondary" onClick={addButton}>
                            + Add Related Button
                        </button>
                    </div>

                    {/* Save Button */}
                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            "Save Page"
                        )}
                    </button>

                    {/* Messages */}
                    {successMessage && (
                        <p className="text-success mt-2">{successMessage}</p>
                    )}
                    {success && !successMessage && (
                        <p className="text-success mt-2">✅ Page saved successfully!</p>
                    )}
                    {error && <p className="text-danger mt-2">❌ Error: {error}</p>}
                </div>
            </div>
        </div>
    );
};

export default EditPageEditor;